"use client";

import {
    useQuery,
    useMutation,
    useQueryClient,
    UseQueryOptions,
    UseMutationOptions,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { toast } from "sonner";
import {
    getHeroOptions,
    addHeroSlide,
    updateHeroSlideAdmin,
    deleteHeroSlideAdmin,
    carouselQueryKeys,
} from "../api/carousel.api";
import type { HeroSlide } from "@/types/settings.types";
import { ProductsMeta } from "@/types/product.types";

interface MutationCallbacks<TData, TVars, TCtx = unknown> {
    onSuccess?: (data: TData, variables: TVars, context: TCtx) => void | Promise<unknown>;
    onError?: (error: AxiosError, variables: TVars, context: TCtx) => void | Promise<unknown>;
    onSettled?: (data: TData | undefined, error: AxiosError | null, variables: TVars, context: TCtx) => void | Promise<unknown>;
}

export { carouselQueryKeys };

// ===============================
// GET HERO SLIDES HOOK
// ===============================
export const useHeroSlides = (
    options?: Partial<UseQueryOptions<HeroSlide[], AxiosError>>
) => {
    return useQuery({
        queryKey: carouselQueryKeys.heroSlides(),
        queryFn: getHeroOptions,
        staleTime: 1000 * 60 * 5, // 5 minutes
        ...options,
    });
};

// ===============================
// ADD HERO SLIDE HOOK
// ===============================
export const useAddHeroSlide = (
    options?: Partial<UseMutationOptions<HeroSlide, AxiosError, FormData, unknown>> & MutationCallbacks<HeroSlide, FormData>
) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: addHeroSlide,
        onSuccess: (data, variables, context) => {
            toast.success("Slide ajouté avec succès ! 🚀");
            queryClient.invalidateQueries({ queryKey: carouselQueryKeys.all });
            options?.onSuccess?.(data, variables, context);
        },
        onError: (error, variables, context) => {
            const errorMessage = (error.response?.data as { message?: string })?.message || "Erreur lors de l'ajout du slide.";
            toast.error(errorMessage);
            console.error("Failed to add hero slide:", error);
            options?.onError?.(error, variables, context);
        },
        ...options,
    });
};

// ===============================
// UPDATE HERO SLIDE HOOK
// ===============================
export const useUpdateHeroSlide = (
    options?: Partial<UseMutationOptions<HeroSlide, AxiosError, { id: string; data: FormData }, unknown>> & MutationCallbacks<HeroSlide, { id: string; data: FormData }>
) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }) => updateHeroSlideAdmin(id, data),
        onSuccess: (data, variables, context) => {
            toast.success("Slide mis à jour avec succès ! 🚀");
            queryClient.invalidateQueries({ queryKey: carouselQueryKeys.all });
            options?.onSuccess?.(data, variables, context);
        },
        onError: (error, variables, context) => {
            const errorMessage = (error.response?.data as { message?: string })?.message || "Erreur lors de la mise à jour du slide.";
            toast.error(errorMessage);
            console.error("Failed to update hero slide:", error);
            options?.onError?.(error, variables, context);
        },
        ...options,
    });
};

// ===============================
// DELETE HERO SLIDE HOOK
// ===============================
export const useDeleteHeroSlide = (
    options?: Partial<UseMutationOptions<void, AxiosError, string, unknown>> & MutationCallbacks<void, string>
) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: deleteHeroSlideAdmin,
        onSuccess: (data, variables, context) => {
            toast.success("Slide supprimé avec succès ! 🚀");
            queryClient.invalidateQueries({ queryKey: carouselQueryKeys.all });
            options?.onSuccess?.(data, variables, context);
        },
        onError: (error, variables, context) => {
            const errorMessage = (error.response?.data as { message?: string })?.message || "Erreur lors de la suppression du slide.";
            toast.error(errorMessage);
            console.error("Failed to delete hero slide:", error);
            options?.onError?.(error, variables, context);
        },
        ...options,
    });
};
